import { useFormikContext } from 'formik'
import get from 'lodash-es/get'
import uniqueBy from 'lodash-es/uniqBy'
import { reverse } from 'named-urls'
import React, { useEffect } from 'react'
import { useParams } from 'react-router-dom'

import { useDashboardPermissions } from '@components/utils/DashboardPermissions'
import { useModalContext } from '@fourthwall/components'
import { routing } from '@utils/routing'

import { useAdminModeContext } from '../../../../App/providers/AdminModeProvider'
import { useSubscription } from '../../../modules/Subscriptions'
import {
  getCosts,
  getCostsPerSize,
  getFulfillmentType,
  getSameSizesHaveDifferentCosts,
  getSizesWithHigherCosts,
  isFourthwallFulfilled,
} from '../../Product.utils'
import { useOffer } from '../../hooks/useOffer'
import type { ApplyToAllVariantsProps, PricingType, ProductFormState } from '../SingleProduct.types'
import { getHasDifferentSizes } from '../SingleProduct.utils'
import { useCustomizationQuery } from '../hooks/useCustomizationQuery'
import { useProductBySlugQuery } from '../hooks/useProductBySlugQuery'
import { ProductPricingView } from './ProductPricingView'
import { CalculateProfit } from './components/CalculateProfit'
import type { PerSizePricingEntryData } from './components/CalculateProfitForPerSizeProduct/CalculateProfitForPerSizeProduct'
import { CalculateProfitForPerSizeProduct } from './components/CalculateProfitForPerSizeProduct/CalculateProfitForPerSizeProduct'
import type { ProductPricingViewProps } from './types'
import { calculateProfit } from './utils/calculateProfit'
import { filterVariantsBySize } from './utils/filterVariantsBySize'
import { getSizePriceSuggestion } from './utils/getSizePriceSuggestion'
import { getTableHeaders } from './utils/getTableHeaders'

export type ProductPricingContainerProps = {
  pricingType: PricingType
  togglePricingType: () => void
  applyToAllVariants: (props: ApplyToAllVariantsProps) => void
}

export const ProductPricingContainer = ({
  pricingType,
  togglePricingType,
  applyToAllVariants,
}: ProductPricingContainerProps) => {
  const { productId = '' } = useParams<'productId'>()
  const { offer } = useOffer(productId)
  const { activeSubscription } = useSubscription()
  const digitalProductsFeePercentage = activeSubscription?.plan.digitalProductsFeePercent

  // context hooks
  const adminMode = useAdminModeContext()
  const { hasPermission } = useDashboardPermissions()
  const { open } = useModalContext()
  const {
    errors,
    values: {
      variants,
      fulfillmentService,
      sellingPriceValue,
      isCompareAtPriceEnabled,
      isCreatorDeclaredCostEnabled,
    },
    setFieldValue,
  } = useFormikContext<ProductFormState>()

  const costsPerSize = getCostsPerSize(offer.variants)
  const sameSizesHaveDifferentCosts = getSameSizesHaveDifferentCosts(costsPerSize)
  const productCosts = getCosts(offer.variants)
  const sizesWithHigherCosts = getSizesWithHigherCosts(offer.variants, productCosts)
  // react-query hooks
  // TODO: this is a temporary solution, we should use the FLAG that is set in the
  // fw-product admin to determine if we should show Explicit or Implicit pricing
  const { customizationQuery, customization } = useCustomizationQuery(offer.customizationId)
  const { productBySlugQuery } = useProductBySlugQuery([customization?.product.slug!!], {
    enabled: customizationQuery.isSuccess,
  })

  const isLoading = customizationQuery.isInitialLoading || productBySlugQuery.isInitialLoading

  // computed values
  const isOnlyExplicitPricingType = productBySlugQuery.data?.pricingType === 'MULTI_PRICE'
  const data = filterVariantsBySize(variants)
  const sizeVariant = offer.variantTypes.find((variant) => variant.type === 'SIZE')
  const hasDifferentSizes = getHasDifferentSizes(sizeVariant)
  const isMissingCosts =
    !!productCosts && productCosts.includes(null) && !!offer?.isSettingCostsRequired
  const sizePriceSuggestion = getSizePriceSuggestion(
    hasDifferentSizes,
    sizesWithHigherCosts,
    isFourthwallFulfilled(getFulfillmentType(fulfillmentService)),
  )
  const isAdmin = !!adminMode?.isActive
  const isDisabled = !hasPermission('products.edit.details')

  // NOTES: For per size products, we always want to show ONLY the explicit pricing type
  useEffect(() => {
    if (isOnlyExplicitPricingType && pricingType === 'IMPLICIT') {
      togglePricingType()
    }
  }, [isOnlyExplicitPricingType, pricingType])

  const handlePriceBlur: ProductPricingViewProps['onPriceBlur'] = (value) => {
    return applyToAllVariants({ priceValue: value })
  }

  const handleIsCompareAtPriceEnabledValueChange: ProductPricingViewProps['onIsCompareAtPriceEnabledValueChange'] =
    (value) => {
      if (!value) {
        applyToAllVariants({ compareAtPriceValue: null })

        return
      }

      setFieldValue('compareAtPriceValue', sellingPriceValue)
      applyToAllVariants({ compareAtPriceValue: sellingPriceValue })
    }

  const handleCompareAtPriceBlur: ProductPricingViewProps['onCompareAtPriceBlur'] = (value) => {
    return applyToAllVariants({ compareAtPriceValue: value })
  }

  const openCalculateProfitModal = () => {
    if (isOnlyExplicitPricingType) {
      const perSizePrice: PerSizePricingEntryData[] = variants.map((variant) => ({
        price: variant.price.value.toString(),
        cost: variant.cost?.value || 0,
        label: variant.size || '',
      }))
      const uniquePerSizePrice = uniqueBy(perSizePrice, 'label')

      open(CalculateProfitForPerSizeProduct, {
        perSizePrice: uniquePerSizePrice,
      })

      return
    }

    open(CalculateProfit, {
      productId,
      pricingType,
      sizePriceSuggestion: pricingType === 'IMPLICIT' ? sizePriceSuggestion : undefined,
      initialPriceValue: variants[0].price.value,
    })
  }

  const handleToggleView = () => {
    applyToAllVariants({})
    togglePricingType()
  }

  return (
    <ProductPricingView
      allowExplicitType={hasDifferentSizes && !isMissingCosts && !sameSizesHaveDifferentCosts}
      onlyExplicitTypeAvailable={isOnlyExplicitPricingType}
      implicitSellingPriceError={get(errors, 'sellingPriceValue')}
      pricingType={pricingType}
      sizePriceSuggestion={sizePriceSuggestion}
      isAdmin={isAdmin}
      isLoading={isLoading}
      profit={calculateProfit(variants, offer.offerType, digitalProductsFeePercentage)}
      costsLink={reverse(routing.products.all.product.costs, { productId })}
      data={data}
      tableHeaders={getTableHeaders({
        variants: offer.variants,
        errors,
        applyToAllVariants,
        disabled: isDisabled,
        showCompareAtPrice: isCompareAtPriceEnabled,
        showProfit: hasPermission('contributions.profit'),
      })}
      showMissingCostsBanner={['ON_DEMAND', 'MOQ'].includes(offer.offerType) && isMissingCosts}
      disabled={isDisabled}
      isCompareAtPriceEnabled={isCompareAtPriceEnabled}
      isCreatorDeclaredCostEnabled={isCreatorDeclaredCostEnabled}
      applyToAllVariants={applyToAllVariants}
      onLearnMoreClick={openCalculateProfitModal}
      onToggleView={handleToggleView}
      onPriceBlur={handlePriceBlur}
      onIsCompareAtPriceEnabledValueChange={handleIsCompareAtPriceEnabledValueChange}
      onCompareAtPriceBlur={handleCompareAtPriceBlur}
    />
  )
}
